import { CommonModule } from '@angular/common';
import { Component, afterNextRender, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { initFlowbite } from 'flowbite';
import { finalize } from 'rxjs/operators';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { FormSubmissionRecord } from '../../../shared/models/form-record';
import { FormsService } from '../../../shared/services/forms.service';
import { UserDataService } from '../../../shared/services/userData.service';

type SortKey = 'id' | 'form_name' | 'date_submitted' | 'user_name';

@Component({
  selector: 'app-form-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, SpinnerComponent, PopupMessageComponent],
  templateUrl: './form-dashboard.component.html'
})
export class FormDashboardComponent {
  private readonly formsService = inject(FormsService);
  private readonly userDataService = inject(UserDataService);
  readonly formOptions = [
    { label: 'Site Security', formName: 'site-security' },
    { label: 'Physical Security', formName: 'physical-security' }
  ];

  readonly records = signal<FormSubmissionRecord[]>([]);
  readonly searchQuery = signal('');
  readonly sortKey = signal<SortKey>('date_submitted');
  readonly sortAscending = signal(false);
  readonly page = signal(1);
  readonly itemsPerPage = 10;
  readonly loading = signal(true);
  readonly buildingId = computed(() => this.userDataService.getBuildingId() ?? '');
  readonly messageModal = signal<string | null>(null);
  readonly popupMessage = signal<string | null>(null);

  readonly filteredRecords = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const sortKey = this.sortKey();
    const ascending = this.sortAscending();

    const filtered = this.records().filter((record) => {
      if (!query) {
        return true;
      }

      return [
        String(record.id),
        record.user_name ?? '',
        record.form_name ?? ''
      ].some((value) => value.toLowerCase().includes(query));
    });

    return [...filtered].sort((left, right) => {
      const leftValue = String(left[sortKey]).toLowerCase();
      const rightValue = String(right[sortKey]).toLowerCase();

      if (leftValue < rightValue) {
        return ascending ? -1 : 1;
      }
      if (leftValue > rightValue) {
        return ascending ? 1 : -1;
      }
      return 0;
    });
  });

  readonly paginatedRecords = computed(() => {
    const startIndex = (this.page() - 1) * this.itemsPerPage;
    return this.filteredRecords().slice(startIndex, startIndex + this.itemsPerPage);
  });

  readonly totalPages = computed(() => {
    const total = Math.ceil(this.filteredRecords().length / this.itemsPerPage);
    return Math.max(total, 1);
  });

  readonly pageNumbers = computed(() => {
    return Array.from({ length: this.totalPages() }, (_, index) => index + 1);
  });

  constructor() {
    const buildingId = this.userDataService.getBuildingId();
    if (!buildingId) {
      this.loading.set(false);
    } else {
      this.loading.set(true);
      this.formsService.getAllFormsRecords(buildingId).pipe(
        finalize(() => {
          this.loading.set(false);
        })
      ).subscribe({
        next: (records) => {
          this.records.set(records);
        },
        error: (error) => {
          this.messageModal.set('unsuccess');
          this.popupMessage.set(error.message);
          console.error('Error loading form records:', error.message);
        }
      });
    }

    afterNextRender(() => {
      initFlowbite();
    });
  }

  updateSearch(query: string): void {
    this.searchQuery.set(query);
    this.page.set(1);
  }

  sortBy(key: SortKey): void {
    if (this.sortKey() === key) {
      this.sortAscending.set(!this.sortAscending());
      this.page.set(1);
      return;
    }

    this.sortKey.set(key);
    this.sortAscending.set(true);
    this.page.set(1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages()) {
      return;
    }

    this.page.set(page);
  }

  previousPage(): void {
    this.goToPage(this.page() - 1);
  }

  nextPage(): void {
    this.goToPage(this.page() + 1);
  }

  downloadRecord(record: FormSubmissionRecord): void {
    this.loading.set(true);
    this.formsService.downloadForm({ formId: record.id }).pipe(
      finalize(() => {
        this.loading.set(false);
      })
    ).subscribe({
      next: (blob) => {
        this.triggerBrowserDownload(blob, this.buildDownloadFileName(record));
        this.messageModal.set('success');
        this.popupMessage.set('Form downloaded successfully.');
      },
      error: (error) => {
        this.messageModal.set('unsuccess');
        this.popupMessage.set(error.message);
        console.error('Error downloading form:', error.message);
      }
    });
  }

  resetMessageModal(): void {
    this.messageModal.set(null);
    this.popupMessage.set(null);
  }

  private triggerBrowserDownload(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  }

  private buildDownloadFileName(record: FormSubmissionRecord): string {
    const normalizedFormName = (record.form_name ?? 'form')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return `${normalizedFormName || 'form'}-${record.id}.pdf`;
  }
}
